import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import isBizBrief from '../../../Logic/Form/Sequences/Biz/isBizBrief'
import isBizPlatform from '../../../Logic/Form/Sequences/Biz/isBizPlatorm'
import isDirectEnquiryBrief from '../../../Logic/Form/Sequences/DirectEnquiry/isDirectEnquiryBrief'
import getSubmissionParams from './getSubmissionParams'
import { PartialBrief } from '../../../Types/Brief'
import throwGQLError from '../../../Utilities/Urql/Errors/throwGQLError'
import { useSubmitBriefMutation } from './useSubmitBrief.generated'
import getBriefUserAttributes from '../../../Utilities/User/getBriefUserAttributes'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import pushTagEvent from '../../../Utilities/GoogleTagManager/pushTagEvent'
import { getBizPlatformSubmissionParams, useSubmitBizBriefMutation } from './submitBizPlatformBrief'
import { BIZ_PLATFORM_SERVICE } from '../../../Constants'

type BriefType = 'business' | 'enquiry' | 'leadfeed'

const getBriefTypeForSuccessPage = (brief: PartialBrief): BriefType => {
  if (isBizBrief(brief)) return 'business'

  if (isDirectEnquiryBrief(brief)) return 'enquiry'

  return 'leadfeed'
}

interface GetSuccessPagePathArgs {
  brief: PartialBrief
  lfAccessSecret?: string | null
}

const getSuccessPagePath = ({ brief, lfAccessSecret }: GetSuccessPagePathArgs): string => {
  const type = getBriefTypeForSuccessPage(brief)
  const country = brief.guest?.country ?? brief.country ?? undefined

  const queryParams = new URLSearchParams({
    type,
    ...(lfAccessSecret && { secret: lfAccessSecret }),
    ...(type === 'business' && country && { country }),
  })
  return `/success?${queryParams.toString()}`
}

interface UseSubmitBriefArgs {
  skip: boolean
}
interface UseSubmitBrief {
  loading: boolean
  submitted: boolean
}

const useSubmitBrief = ({ skip }: UseSubmitBriefArgs): UseSubmitBrief => {
  const { brief, dispatchBrief, setSubmitting: setSubmittingInProvider } = useBriefContext()
  const [{ error: lfError, fetching: submittingLF, data: lfData }, submitBrief] =
    useSubmitBriefMutation()
  const [
    { error: bizPlatformError, fetching: submittingBizPlatform, data: bizPlatformData },
    submitBizPlatformBrief,
  ] = useSubmitBizBriefMutation()
  const [submitted, setSubmitted] = useState(false)
  const submitting = submittingLF || submittingBizPlatform
  const navigate = useNavigate()
  const { user } = useGlobalAuth()

  useEffect(() => {
    if (skip || submitted) return
    if (isBizPlatform(brief)) {
      submitBizPlatformBrief(
        { input: getBizPlatformSubmissionParams(brief) },
        { url: BIZ_PLATFORM_SERVICE }
      )
    } else {
      submitBrief({ params: getSubmissionParams(brief) })
    }
    setSubmitted(true)
    setSubmittingInProvider(true)
  }, [skip])

  useEffect(() => {
    if (submitting || (!lfData && !bizPlatformData)) return

    const submittedBrief = lfData?.brief
    const submittedBizPlatformBrief = bizPlatformData?.createEvent
    if (!submittedBrief && !submittedBizPlatformBrief) return // this scenario would trigger the 'no response' error below

    const id = (submittedBrief && parseInt(submittedBrief?.id, 10)) || submittedBizPlatformBrief?.ID
    if (id) pushTagEvent({ ...brief, id }, 'submitBrief')

    const successPath = getSuccessPagePath({
      brief,
      lfAccessSecret: submittedBrief?.lfAccessSecret,
    })

    setSubmittingInProvider(false)
    // 'reset' the brief but leave user attrs intact, in case the user hits the back button
    dispatchBrief({
      overwrite: {
        ...getBriefUserAttributes(user, { newlyRegistered: false }),
      },
    })
    navigate(successPath)
  }, [lfData, bizPlatformData])

  if (lfError) throwGQLError(lfError)
  if (bizPlatformError) throwGQLError(bizPlatformError)

  if (submitted && !submitting && !lfData?.brief?.id && !bizPlatformData?.createEvent?.ID) {
    throw new Error('No response returned for brief on submission')
  }

  return { loading: submitting, submitted }
}

export default useSubmitBrief
